import { useState } from "react";
import { toast } from "sonner";
import { X, Barcode, Camera } from "lucide-react";
import { api, inputCls, rd, askPin, beep } from "./posShared";
import CameraScanner from "./CameraScanner";

export default function ReturnModal({ invoice, products = [], onClose, onDone }) {
  const [qtys, setQtys] = useState(Object.fromEntries(invoice.items.map((i) => [i.product_id, 0])));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [scan, setScan] = useState("");
  const [camera, setCamera] = useState(false);

  const set = (pid, v, max) => setQtys((p) => ({ ...p, [pid]: Math.max(0, Math.min(Number(v) || 0, max)) }));
  const total = invoice.items.reduce((s, i) => s + (qtys[i.product_id] || 0) * i.price, 0);

  // Scan a product to increment its return quantity (up to the amount originally purchased).
  const handleCode = (raw) => {
    const code = String(raw || "").trim().toLowerCase();
    setScan("");
    if (!code) return;
    const prod = products.find(
      (p) => (p.barcode || "").toLowerCase() === code || (p.code || "").toLowerCase() === code
    );
    const item = prod && invoice.items.find((i) => i.product_id === prod.id);
    if (item) {
      const cur = qtys[item.product_id] || 0;
      if (cur >= item.qty) { beep(false); return toast.error(`Ya alcanzaste el máximo devuelto de ${item.name}`); }
      set(item.product_id, cur + 1, item.qty);
      beep(true);
      toast.success(`+1 ${item.name}`);
    } else {
      beep(false);
      toast.error("Ese producto no está en esta factura");
    }
  };
  const onScan = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    handleCode(scan);
  };

  const submit = async () => {
    const items = invoice.items.filter((i) => qtys[i.product_id] > 0).map((i) => ({ product_id: i.product_id, qty: qtys[i.product_id] }));
    if (!items.length) return toast.error("Indica la cantidad a devolver");
    if (!reason.trim()) return toast.error("Indica el motivo de la devolución");
    const pin = await askPin("Ingresa el PIN de administrador o un CÓDIGO DE AUTORIZACIÓN de un solo uso:");
    if (!pin) return toast.error("Se requiere el PIN de administrador o un código de autorización");
    setSaving(true);
    try {
      await api.post(`/pos/invoices/${invoice.id}/return`, { items, reason, pin });
      toast.success("Devolución registrada y stock reingresado");
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar la devolución");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[96] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
          <span className="font-medium">Devolución · {invoice.number}</span>
          <button data-testid="return-close" onClick={onClose} className="text-neutral-400 hover:text-espresso"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-neutral-400">Indica cuántas unidades se devuelven de cada producto. El stock se reingresará automáticamente.</p>
          <div className="relative">
            <input data-testid="return-scan" value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={onScan} placeholder="Escanear código para sumar devolución..." className={`${inputCls} pr-16`} />
            <button data-testid="return-camera" onClick={() => setCamera(true)} title="Escanear con la cámara" className="absolute right-9 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-rose"><Camera size={16} /></button>
            <Barcode size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-rose" />
          </div>
          {invoice.items.map((i) => (
            <div key={i.product_id} className="flex items-center justify-between gap-3 border-b border-neutral-50 py-2">
              <div className="text-sm"><div>{i.name}</div><div className="text-xs text-neutral-400">Comprado: {i.qty} · {rd(i.price)}</div></div>
              <input data-testid={`return-qty-${i.product_id}`} type="number" min={0} max={i.qty} value={qtys[i.product_id]} onChange={(e) => set(i.product_id, e.target.value, i.qty)} className="w-20 bg-white border border-neutral-200 rounded-lg px-2 py-1.5 text-sm text-right" />
            </div>
          ))}
          <div><label className="text-xs uppercase text-neutral-500">Motivo</label><input data-testid="return-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} placeholder="Producto defectuoso, cambio, etc." /></div>
          <div className="flex justify-between text-sm font-medium pt-1"><span>Total a devolver</span><span className="text-rose">{rd(total)}</span></div>
          <button data-testid="return-submit" onClick={submit} disabled={saving} className="w-full bg-espresso text-cream rounded-full py-3 text-xs uppercase tracking-[0.15em] hover:bg-rose transition disabled:opacity-60">
            {saving ? "Registrando..." : "Registrar devolución (PIN)"}
          </button>
        </div>
      </div>
      {camera && <CameraScanner onDetect={handleCode} onClose={() => setCamera(false)} />}
    </div>
  );
}
